/**
 * Holiday List Component
 * 
 * Displays a comprehensive list of holidays for the daycare center
 * Features:
 * - Fetches holiday data from API: users/v1/user-forms/holidays
 * - Uses Redux for state management
 * - Shows holiday status (Past, Today, Upcoming)
 * - Differentiates between full-day closures and early closures
 * - Displays summary statistics
 * - Responsive table design with Bootstrap styling
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Container } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchHolidays, selectHolidays, selectHolidaysLoading, selectHolidaysError, clearError } from '../../../../store/holiday/holidaySlice';

import LoaderModal from '../../../../components/loaderModal';
import NoDataFound from '../../../../components/noDataFound';

//Sweet alert
import Swal from 'sweetalert2'

// Custom CSS for holiday list
import '../../../../assets/css/holiday-list.css'


const HolidayPage = () => {
   const navigate = useNavigate();
   const dispatch = useDispatch();
   
   // Redux state
   const holidays = useSelector(selectHolidays)?.data;
   const session = useSelector(selectHolidays)?.sessionName;
   const loading = useSelector(selectHolidaysLoading);
   const error = useSelector(selectHolidaysError);

   const [isFirst, setIsFirst] = useState(true);
   
   // Scroll to top on mount
   useEffect(() => {
      window.scrollTo({
         top: 0,
         behavior: "smooth",
      });
      setIsFirst(false)
   }, [isFirst]);

   // Load holidays on component mount
   useEffect(() => {
      if (localStorage.getItem("uzoneAuthToken")) {
         dispatch(fetchHolidays({ page: 1, limit: 100 }));
      } else {
         navigate('/')
      }
   }, [dispatch, navigate]);

   // Handle API errors
   useEffect(() => {
      if (error) {
         Swal.fire({
            title: 'Error',
            text: error,
            icon: 'error',
            confirmButtonText: 'Okay'
         }).then(() => {
            dispatch(clearError());
         });
      }
   }, [error, dispatch]);

   // Format date helper function
   const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
   };

   // Format time helper function
   const formatTime = (timeString) => {
      if (!timeString) return null;
      const [hours, minutes] = timeString.split(':');
      const hour24 = parseInt(hours);
      const min = parseInt(minutes);
      
      // Convert 24-hour to 12-hour format
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const formattedMinutes = min.toString().padStart(2, '0');
      
      return `${hour12}:${formattedMinutes} ${ampm}`;
   };

   // Get holiday status badge
   const getHolidayStatusBadge = (holiday) => {
      const today = new Date();
      const holidayDate = new Date(holiday.holiday_date);
      if (holidayDate < today) {
         return <span className="badge bg-secondary text-white">Past</span>;
      } else if (holidayDate.toDateString() === today.toDateString()) {
         return <span className="badge bg-primary text-white">Today</span>;
      } else {
         return <span className="badge bg-primary text-white">Upcoming</span>;
      }
   };

   return (
      <>
         <LoaderModal showLoader={loading} />
         <div id='content-page' className='content-page holiday-list-container'>
            <Container>
               <Row>
                  <Col sm="12">
                     <Card className="holiday-card">
                        <Card.Header className="d-flex justify-content-between bg-primary text-white">
                           <div className="header-title">
                              <h4 className="card-title mb-0 text-white">
                                 <i className="ri-calendar-line me-2 text-white"></i>
                                 Holiday List {session ? ` (${session})` : '' }
                              </h4>
                           </div>
                           <div className="d-flex align-items-center">
                              <span className="text-white">Total: {holidays?.length || 0} holidays</span>
                           </div>
                        </Card.Header>
                        <Card.Body>
                           {holidays?.length > 0 ? (
                              <div className="table-responsive">
                                 <table className="table table-striped table-hover holiday-table">
                                    {/* <thead style={{background: '#fff', color: '#000'}}>
                                       <tr>
                                          <th scope="col">#</th>
                                          <th scope="col">Holiday Name</th>
                                          <th scope="col">Date</th>
                                          <th scope="col">Type</th>
                                          <th scope="col">Status</th>
                                          <th scope="col">Details</th>
                                       </tr>
                                    </thead> */}
                                    <tbody>
                                       {holidays.map((holiday, index) => (
                                          <tr key={holiday.id} className="align-middle">
                                             <th scope="row">{index + 1}</th>
                                             <td>
                                                <div className="d-flex align-items-center">
                                                   <div className="me-3">
                                                      <i className="ri-calendar-event-fill text-secondary fs-4"></i>
                                                   </div>
                                                   <div>
                                                      <h6 className="mb-0">{holiday.name}</h6>
                                                   </div>
                                                </div>
                                             </td>
                                             <td>
                                                <span className="fw-semibold">
                                                   {formatDate(holiday.holiday_date)}
                                                </span>
                                             </td>
                                             <td>
                                                {holiday.is_full_day ? (
                                                   <span className="badge bg-secondary text-white">
                                                      <i className="ri-sun-line me-1"></i>
                                                      Full Day
                                                   </span>
                                                ) : (
                                                   <span className="badge bg-primary text-white">
                                                      <i className="ri-time-line me-1"></i>
                                                      Close Early
                                                   </span>
                                                )}
                                             </td>
                                             <td>
                                                {getHolidayStatusBadge(holiday)}
                                             </td>
                                             <td>
                                                {holiday.is_full_day === 0 && holiday.close_time ? (
                                                   <small className="text-muted">
                                                      <i className="ri-time-line me-1"></i>
                                                      Close at {formatTime(holiday.close_time)}
                                                   </small>
                                                ) : holiday.is_full_day === 1 ? (
                                                   <small className="text-muted">
                                                      <i className="ri-close-circle-line me-1"></i>
                                                      Closed all day
                                                   </small>
                                                ) : null}
                                             </td>
                                          </tr>
                                       ))}
                                    </tbody>
                                 </table>
                              </div>
                           ) : (
                              <NoDataFound />
                           )}
                           
                           {/* Holiday Summary Cards */}
                           {holidays?.length > 0 && (
                              <Row className="mt-4">
                                 <Col md={4}>
                                    <Card className="bg-light border-0">
                                       <Card.Body className="text-center">
                                          <i className="ri-calendar-check-line display-4 text-success mb-2"></i>
                                          <h5 className="mb-1">
                                             {holidays.filter(h => new Date(h.holiday_date) < new Date()).length}
                                          </h5>
                                          <small className="text-muted">Past Holidays</small>
                                       </Card.Body>
                                    </Card>
                                 </Col>
                                 <Col md={4}>
                                    <Card className="bg-light border-0">
                                       <Card.Body className="text-center">
                                          <i className="ri-calendar-event-line display-4 text-primary mb-2"></i>
                                          <h5 className="mb-1">
                                             {holidays.filter(h => new Date(h.holiday_date) > new Date()).length}
                                          </h5>
                                          <small className="text-muted">Upcoming Holidays</small>
                                       </Card.Body>
                                    </Card>
                                 </Col>
                                 <Col md={4}>
                                    <Card className="bg-light border-0">
                                       <Card.Body className="text-center">
                                          <i className="ri-time-line display-4 text-warning mb-2"></i>
                                          <h5 className="mb-1">
                                             {holidays.filter(h => h.is_full_day === 0).length}
                                          </h5>
                                          <small className="text-muted">Early Close Days</small>
                                       </Card.Body>
                                    </Card>
                                 </Col>
                              </Row>
                           )}
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </Container>
         </div>
      </>
   )
}

export default HolidayPage